import { useState, useCallback, useRef, useEffect } from 'react'
import { motion, useMotionValue, animate, useMotionValueEvent } from 'framer-motion'
import { useSliderKeyboard } from '@/hooks/useSliderKeyboard'
import { SlideTitleLines } from '@/components/sections/SlideTitleLines'
import SliderSwipePrompt from '@/components/SliderSwipePrompt'

const services = [
  {
    id: '01',
    category: 'Identity',
    title: 'Visual Identity',
    titleLines: ['Visual', 'Identity'],
    description: "Logo, colour, typography, and visual language. We build identity systems that are distinctive, scalable, and built to last — not trends that date in a year.",
    tags: ['Logo Design', 'Brand Guidelines', 'Typography']
  },
  {
    id: '02',
    category: 'System',
    title: 'Brand Systems',
    titleLines: ['Brand', 'Systems'],
    description: "Identity that scales. We design flexible systems — templates, components, and guidelines — so your team can execute consistently without losing the magic.",
    tags: ['Templates', 'Design Tokens', 'Documentation']
  },
  {
    id: '03',
    category: 'Experience',
    title: 'Digital Branding',
    titleLines: ['Digital', 'Branding'],
    description: "Your brand across every screen. We extend your identity into web, app, and digital experiences — ensuring recognition and impact at every touchpoint.",
    tags: ['Web Design', 'App UI', 'Digital Assets']
  },
  {
    id: '04',
    category: 'Direction',
    title: 'Art Direction',
    titleLines: ['Art', 'Direction'],
    description: "Every asset on-brand. We define the look, feel, and creative direction for your campaigns, content, and collateral so everything speaks with one voice.",
    tags: ['Creative Direction', 'Photography', 'Campaigns']
  },
]

export default function DesignServices() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [displayedSlide, setDisplayedSlide] = useState(0)
  const [slideWidth, setSlideWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const isDraggingRef = useRef(false)

  useMotionValueEvent(x, 'change', (latest) => {
    if (slideWidth > 0) {
      const slide = Math.round(-latest / slideWidth)
      setDisplayedSlide(Math.max(0, Math.min(slide, services.length - 1)))
    }
  })

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(Math.max(0, Math.min(index, services.length - 1)))
  }, [])

  const goPrev = useCallback(() => {
    goToSlide(currentSlide - 1)
  }, [currentSlide, goToSlide])

  const goNext = useCallback(() => {
    goToSlide(currentSlide + 1)
  }, [currentSlide, goToSlide])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? container.offsetWidth
      setSlideWidth(width)
    })
    observer.observe(container)
    setSlideWidth(container.offsetWidth)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isDraggingRef.current && slideWidth > 0) {
      animate(x, -currentSlide * slideWidth, { type: 'spring', stiffness: 300, damping: 30 })
    }
  }, [currentSlide, slideWidth, x])

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true
  }, [])

  const handleDragEnd = useCallback((_: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number }; velocity: { x: number } }) => {
    isDraggingRef.current = false
    if (slideWidth === 0) return
    const threshold = slideWidth * 0.15
    const velocityThreshold = 500
    const shouldGoPrev = info.offset.x > threshold || info.velocity.x > velocityThreshold
    const shouldGoNext = info.offset.x < -threshold || info.velocity.x < -velocityThreshold
    if (shouldGoPrev) goToSlide(currentSlide - 1)
    else if (shouldGoNext) goToSlide(currentSlide + 1)
  }, [currentSlide, goToSlide, slideWidth])

  useSliderKeyboard(containerRef, goPrev, goNext, true)

  return (
    <section id="services" className="pt-24 md:pt-32 bg-white border-t border-slate-200 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('/assets/bg-noise.png')] opacity-[0.03] mix-blend-overlay pointer-events-none" />

      <div className="container relative z-10 px-4 md:px-6 text-center md:text-left">
        <h2 className="text-sm font-bold text-accent uppercase tracking-[0.2em] mb-6">Our Services</h2>
        <h3 className="text-4xl md:text-7xl font-bold font-heading text-primary max-w-4xl mx-auto md:mx-0 leading-[0.9] uppercase tracking-tight">
          Design That <br /> <span className="text-accent">Demands Attention.</span>
        </h3>
      </div>

      <div ref={containerRef} className="relative z-10 w-full">
        <SliderSwipePrompt>
        <div className="relative overflow-hidden cursor-grab active:cursor-grabbing">
          <motion.div
            className="relative z-10 flex"
            style={{ x }}
            drag="x"
            dragConstraints={slideWidth > 0 ? { left: -(services.length - 1) * slideWidth, right: 0 } : undefined}
            dragElastic={0.15}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            whileTap={{ cursor: 'grabbing' }}
          >
            {services.map((service) => (
              <div
                key={service.id}
                className="w-full flex-shrink-0 min-w-full min-h-[420px] md:min-h-[480px] flex items-center py-12 md:py-16"
              >
                <div className="container px-4 md:px-6 w-full">
                  <div className="flex flex-col lg:flex-row items-center md:items-start gap-8 md:gap-16">
                    <div className="lg:flex-1 text-center md:text-left">
                      <div className="text-sm font-mono text-accent mb-1">
                        /{service.id} {service.category}
                      </div>
                      <h4 className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading text-primary uppercase tracking-tight">
                        <SlideTitleLines line1={service.titleLines[0]} line2={service.titleLines[1]} />
                      </h4>
                    </div>
                    <div className="lg:max-w-xl text-center md:text-left">
                      <div className="flex flex-wrap gap-2 mb-6 justify-center md:justify-start">
                        {service.tags.map(tag => (
                          <span key={tag} className="text-xs border border-slate-200 px-3 py-1 rounded-full text-slate-600 uppercase tracking-wide">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <p className="text-slate-600 leading-relaxed text-lg">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
        </SliderSwipePrompt>

        <div className="container px-4 md:px-6 pt-4 md:pt-6 pb-6 flex items-center justify-between gap-4">
          <div className="flex-1 flex justify-start min-w-0">
            {displayedSlide > 0 ? (
              <motion.button
                type="button"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                onClick={() => goToSlide(displayedSlide - 1)}
                className="cursor-pointer text-left group flex items-center gap-1.5 md:gap-2"
                aria-label={`Previous: ${services[displayedSlide - 1].title}`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent flex-shrink-0 w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                <h4 className="text-[10px] sm:text-xs md:text-sm lg:text-base font-bold font-heading text-accent group-hover:text-accent/80 uppercase tracking-tight transition-colors text-left">
                  {services[displayedSlide - 1].titleLines[0]}
                  {' '}
                  <br className="sm:hidden" />
                  {services[displayedSlide - 1].titleLines[1]}
                </h4>
              </motion.button>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-accent w-6'
                    : 'bg-slate-300 w-1.5 hover:bg-slate-400'
                }`}
                aria-label={`Go to service ${index + 1}`}
              />
            ))}
          </div>
          <div className="flex-1 flex justify-end min-w-0">
            {displayedSlide < services.length - 1 ? (
              <motion.button
                type="button"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                onClick={() => goToSlide(displayedSlide + 1)}
                className="cursor-pointer text-right group flex items-center gap-1.5 md:gap-2 flex-row-reverse"
                aria-label={`Next: ${services[displayedSlide + 1].title}`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent flex-shrink-0 w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4">
                  <path d="M9 18l6-6-6-6" />
                </svg>
                <h4 className="text-[10px] sm:text-xs md:text-sm lg:text-base font-bold font-heading text-accent group-hover:text-accent/80 uppercase tracking-tight transition-colors text-right">
                  {services[displayedSlide + 1].titleLines[0]}
                  {' '}
                  <br className="sm:hidden" />
                  {services[displayedSlide + 1].titleLines[1]}
                </h4>
              </motion.button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}
